import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";

import { useWorkspaceSettingsStore } from "../../store/useWorkspaceSettingsStore";
import { blockStudent, unblockStudent } from "../../api/workspaceSettings";
import { BlockStudentDialog } from "./BlockStudentDialog";

import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/utils/customToast";
import useUserStore from "@/features/auth/store/useUserStore";

export const IndividualWorkspaceMembersCard = () => {
  const { t } = useTranslation(["workspaces", "common"]);
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { settings, setSettings } = useWorkspaceSettingsStore();
  const { user } = useUserStore();
  const [selectedStudent, setSelectedStudent] = useState<{
    id: string;
    name: string;
    workspaceUserId: string;
  } | null>(null);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [processingUserId, setProcessingUserId] = useState<string | null>(null);

  if (!settings || !user) return null;

  const { members, user_status } = settings;
  const isTutor = user.role === "tutor";

  // Check if tutor can manage students (must be active and not blocked/left)
  const canManageStudents =
    isTutor &&
    user_status.is_active_for_user &&
    !user_status.is_blocked_for_user &&
    !user_status.has_user_left;

  // Get the other member (not the current user)
  const otherMember = members.find((member) => member.id !== user.id);

  if (!otherMember) {
    // If current user is student, we expect a tutor, otherwise we expect a student
    const expectedRole = user.role === "student" ? "tutor" : "student";
    const cardTitle = t(`common:roles.${expectedRole}`, expectedRole === "tutor" ? "Tutor" : "Student");
    
    return (
      <div className="bg-foreground/50 text-accent-foreground relative z-50 rounded-xl border-none p-5 shadow-lg backdrop-blur-xl">
        {/* Background Pattern */}
        <div className="absolute inset-0 overflow-hidden rounded-xl opacity-5">
          <div className="absolute -top-12 -left-12 h-24 w-24 rounded-full bg-gradient-to-br from-violet-600 to-pink-500" />
          <div className="absolute -bottom-16 -right-16 h-32 w-32 rounded-full bg-gradient-to-tr from-indigo-600 to-purple-500" />
        </div>

        <div className="relative">
          <div className="flex items-center gap-4">
            <div className="bg-muted/20 flex items-center justify-center rounded-xl p-2 backdrop-blur-sm">
              <Icon
                className="text-accent-foreground"
                height={"1.5rem"}
                icon="mdi:account-multiple"
                width={"1.5rem"}
              />
            </div>
            
            <div className="flex flex-col justify-center">
              <h3 className="text-accent-foreground text-base font-semibold">{cardTitle}</h3>
            </div>
          </div>
          
          <div className="mt-3.5 text-sm">
            <p className="text-muted-foreground">
              {t("workspaces:settings.members.noOtherMembers", "No other members in this workspace")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isOtherMemberBlocked = Boolean(otherMember.blocked_at);
  const isOtherMemberLeft = Boolean(otherMember.left_at);
  const isOtherMemberStudent = otherMember.type === "student";
  const canBlockOtherMember = canManageStudents && isOtherMemberStudent && !isOtherMemberLeft;
  const isProcessing = processingUserId === otherMember.id;

  const handleBlockStudent = () => {
    if (!otherMember.workspace_user_id) return;
    
    setSelectedStudent({
      id: otherMember.id,
      name: otherMember.full_name,
      workspaceUserId: otherMember.workspace_user_id,
    });
    setBlockDialogOpen(true);
  };

  const handleUnblockStudent = async () => {
    if (!workspaceId || !otherMember.workspace_user_id) return;

    setProcessingUserId(otherMember.id);
    try {
      await unblockStudent(workspaceId, otherMember.workspace_user_id, null);

      // Update local state
      setSettings({
        ...settings,
        members: settings.members.map((m) =>
          m.id === otherMember.id
            ? {
                ...m,
                blocked_at: null,
                blocked_reason: null,
                blocked_by: null,
              }
            : m,
        ),
      });

      toast.success(
        t("workspaces:settings.members.studentUnblocked", "Student unblocked successfully"),
      );
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        t("workspaces:settings.members.unblockFailed", "Failed to unblock student");

      toast.error(errorMessage);
    } finally {
      setProcessingUserId(null);
    }
  };

  const onBlockConfirm = async (reason?: string) => {
    if (!workspaceId || !selectedStudent?.workspaceUserId) return;

    try {
      const data = reason ? { reason } : null;

      await blockStudent(workspaceId, selectedStudent.workspaceUserId, data);

      // Update local state
      setSettings({
        ...settings,
        members: settings.members.map((m) =>
          m.id === selectedStudent.id
            ? {
                ...m,
                blocked_at: new Date().toISOString(),
                blocked_reason: reason || null,
                blocked_by: user.id,
              }
            : m,
        ),
      });

      toast.success(
        t("workspaces:settings.members.studentBlocked", "Student blocked successfully"),
      );
    } catch (error: any) {
      throw error; // Re-throw to let BlockStudentDialog handle it
    }
  };

  const getMemberStatusBadge = () => {
    if (isOtherMemberLeft) {
      return (
        <Badge className="text-xs" variant="outline">
          {t("workspaces:settings.members.left", "Left")}
        </Badge>
      );
    }
    
    if (isOtherMemberBlocked) {
      return (
        <Badge className="text-xs" variant="destructive">
          {t("workspaces:settings.members.blocked", "Blocked")}
        </Badge>
      );
    }

    return null;
  };

  const getActionButton = () => {
    if (!canBlockOtherMember) return null;

    if (isOtherMemberBlocked) {
      return (
        <Button
          className="h-8 gap-2 px-3 text-xs"
          disabled={isProcessing}
          size="sm"
          variant="outline"
          onClick={handleUnblockStudent}
        >
          {isProcessing ? (
            <Icon className="animate-spin" icon="lucide:loader-2" />
          ) : (
            <Icon icon="mdi:shield-check" />
          )}
          {t("workspaces:settings.members.unblock", "Unblock")}
        </Button>
      );
    }

    return (
      <Button
        className="h-8 gap-2 px-3 text-xs"
        disabled={isProcessing}
        size="sm"
        variant="destructive"
        onClick={handleBlockStudent}
      >
        {isProcessing ? (
          <Icon className="animate-spin" icon="lucide:loader-2" />
        ) : (
          <Icon icon="mdi:block-helper" />
        )}
        {t("workspaces:settings.members.block", "Block")}
      </Button>
    );
  };

  // Get the title based on the other member's role
  const cardTitle = t(`common:roles.${otherMember.type}`, otherMember.type === "tutor" ? "Tutor" : "Student");

  return (
    <>
      <div className="bg-foreground/50 text-accent-foreground relative z-50 rounded-xl border-none p-5 shadow-lg backdrop-blur-xl">
        {/* Background Pattern */}
        <div className="absolute inset-0 overflow-hidden rounded-xl opacity-5">
          <div className="absolute -top-12 -left-12 h-24 w-24 rounded-full bg-gradient-to-br from-violet-600 to-pink-500" />
          <div className="absolute -bottom-16 -right-16 h-32 w-32 rounded-full bg-gradient-to-tr from-indigo-600 to-purple-500" />
        </div>

        <div className="relative">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="bg-muted/20 flex items-center justify-center rounded-xl p-2 backdrop-blur-sm">
                <Icon
                  className="text-accent-foreground"
                  height={"1.5rem"}
                  icon="mdi:account-multiple"
                  width={"1.5rem"}
                />
              </div>
              
              <div className="flex flex-col justify-center">
                <h3 className="text-accent-foreground text-base font-semibold">{cardTitle}</h3>
              </div>
            </div>
            {getActionButton() && <div className="my-auto flex-shrink-0 text-sm">{getActionButton()}</div>}
          </div>
          
          <div className="mt-3.5">
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="group bg-muted/30 hover:bg-muted/40 relative overflow-hidden rounded-xl p-4 transition-all duration-300"
              initial={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {/* Background blur effect */}
              <div className="absolute inset-0 top-1/2 -left-0 h-32 w-32 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full opacity-20">
                <img
                  alt={otherMember.full_name}
                  className="h-full w-full object-cover blur-2xl"
                  src={otherMember.avatar_path || otherMember.avatar || "/placeholder.svg"}
                />
              </div>

              <div className="relative flex items-center gap-4">
                <Avatar className="h-12 w-12">
                  <AvatarImage
                    alt={otherMember.full_name}
                    src={otherMember.avatar_path || otherMember.avatar || "/placeholder.svg"}
                  />
                  <AvatarFallback>
                    {otherMember.full_name
                      .split(" ")
                      .map((n: string) => n[0])
                      .join("")}
                  </AvatarFallback>
                </Avatar>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-accent-foreground truncate text-base font-medium">
                      {otherMember.full_name}
                    </h4>
                    {getMemberStatusBadge()}
                  </div>
                  {otherMember.blocked_reason && isOtherMemberBlocked && (
                    <p className="text-muted-foreground mt-1 text-sm">
                      {t("workspaces:settings.members.blockReason", "Reason")}: {otherMember.blocked_reason}
                    </p>
                  )}
                  {otherMember.joined_at && (
                    <p className="text-muted-foreground text-xs">
                      {t("workspaces:settings.members.joinedAt", "Joined")} {new Date(otherMember.joined_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <BlockStudentDialog
        isOpen={blockDialogOpen}
        studentName={selectedStudent?.name || ""}
        onClose={() => {
          setBlockDialogOpen(false);
          setSelectedStudent(null);
        }}
        onConfirm={onBlockConfirm}
      />
    </>
  );
};